import { MutableRefObject, useEffect } from 'react';
import { generateChatTitle } from '../../../services/api/chatHistory.api';
import { generateUniqueKey } from '../utils/helpers';

interface Options {
  email: string | null;
  urlSessionId: string | undefined;
  // sessionIdRef/hasUpdatedUrl/isNewChatRef/newChatSessionIdRef are created
  // in Chat.tsx, not here — isNewChatRef has to exist before useWebSocket is
  // called (it's passed in as a constructor arg), but this hook needs
  // agentResponseCountRef *out of* useWebSocket, so the refs can't be
  // created and consumed in the same hook without a circular dependency.
  // Same pattern as chatMessagesRef/messagesEndRef elsewhere in Chat.tsx.
  sessionIdRef: MutableRefObject<string | null>;
  hasUpdatedUrl: MutableRefObject<boolean>;
  isNewChatRef: MutableRefObject<boolean>;
  newChatSessionIdRef: MutableRefObject<string | null>;
  agentResponseCountRef: MutableRefObject<number>;
  // Cross-hook resets that only apply when switching TO an existing history
  // session (not on a brand-new conversation) — composed by Chat.tsx from
  // useFileUpload/useModelSelector/useDocPreview's own reset/close functions.
  onSessionSwitch?: () => void;
}

// Best-effort: a brand-new chat that got exactly one exchange before being
// abandoned (via history navigation or "New chat") never reaches the "2nd
// response" trigger in useWebSocket.tsx — this names it on the way out
// instead. Shared between the session-switch effect and
// resetSessionForNewChat, which were previously two copies of the same
// logic.
const generateTitleForAbandonedChat = (
  leavingSessionId: string | null,
  leavingWasNewChat: boolean,
  leavingResponseCount: number,
) => {
  if (leavingSessionId && leavingWasNewChat && leavingResponseCount === 1) {
    void generateChatTitle(leavingSessionId)
      .then((result) => {
        if (result) window.dispatchEvent(new CustomEvent('chat:session_created'));
      })
      .catch(() => { /* best-effort — sidebar just won't show a name yet */ });
  }
};

export const useChatSession = ({
  email,
  urlSessionId,
  sessionIdRef,
  hasUpdatedUrl,
  isNewChatRef,
  newChatSessionIdRef,
  agentResponseCountRef,
  onSessionSwitch,
}: Options) => {
  // ── Session ID + isNewChat setup ──────────────────────────────────────────
  // Runs once when email becomes available (and again if urlSessionId changes,
  // which only happens when navigating between history sessions).
  //
  // History chat  (/chat/:sessionId present):
  //   - Use the session ID from the URL
  //   - Mark URL as already correct (hasUpdatedUrl = true)
  //   - isNewChat = FALSE — this is a continuation from history
  //
  // New chat (/chat, no sessionId param):
  //   - Generate a fresh UUID
  //   - isNewChat = TRUE — stays true for every message in this conversation
  //   - URL will be updated silently on first send via pushSessionUrl
  useEffect(() => {
    if (!email) return;

    // Capture the session we're leaving (if any) before overwriting the
    // refs below — this effect fires both on first mount (nothing to
    // capture, sessionIdRef is still null) and whenever urlSessionId
    // changes to a DIFFERENT session (sidebar history click), which is
    // exactly the "abandoned a new chat via navigation" case that needs a
    // title-generation request, same as resetSessionForNewChat's.
    // (If the transition instead came from "New chat", that handler
    // already nulled sessionIdRef and did its own check first, so
    // leavingSessionId is null here and this is a no-op — no double-fire.)
    const leavingSessionId = sessionIdRef.current;
    const leavingWasNewChat = isNewChatRef.current;
    const leavingResponseCount = agentResponseCountRef.current;

    if (urlSessionId) {
      sessionIdRef.current = urlSessionId;
      hasUpdatedUrl.current = true;
      // If we're navigating back to the current new chat, keep is_new_chat true
      isNewChatRef.current = urlSessionId === newChatSessionIdRef.current;
      // Cross-hook resets — see this option's own comment.
      onSessionSwitch?.();
    } else if (!sessionIdRef.current) {
      // Brand new conversation — generate fresh UUID
      sessionIdRef.current = generateUniqueKey();
      newChatSessionIdRef.current = sessionIdRef.current;
      isNewChatRef.current = true;
    }

    // The counter tracks the CURRENT session's live response count — reset
    // it on every switch regardless of whether the leaving session
    // qualifies for naming below (see agentResponseCountRef's own comment
    // in useWebSocket).
    agentResponseCountRef.current = 0;

    generateTitleForAbandonedChat(leavingSessionId, leavingWasNewChat, leavingResponseCount);
  }, [email, urlSessionId]);

  // ── URL update helper ─────────────────────────────────────────────────────
  // Called once per NEW session, right before the first WS payload is sent.
  //
  // KEY: Uses window.history.replaceState instead of navigate() to update the
  // URL bar WITHOUT triggering a React Router re-render. If we used navigate()
  // here, React Router would see the new /chat/:sessionId URL, re-mount Chat
  // with urlSessionId set, and the history useEffect would call setMessages([])
  // — wiping the user's message before the WS response arrives.
  //
  // No-op for existing sessions since hasUpdatedUrl is set to true on mount.
  const pushSessionUrl = () => {
    if (!hasUpdatedUrl.current && sessionIdRef.current) {
      hasUpdatedUrl.current = true;
      // Update URL silently — no re-render, no remount.
      // Sidebar refresh is handled by useWebSocket on first response, not here.
      window.history.replaceState(null, '', `/chat/${sessionIdRef.current}`);
    }
  };

  // Used by the chat:new handler in Chat.tsx. Resets session identity so the
  // next send starts completely fresh, and fires the same abandoned-chat
  // title generation as the effect above.
  const resetSessionForNewChat = () => {
    const leavingSessionId = sessionIdRef.current;
    const leavingWasNewChat = isNewChatRef.current;
    const leavingResponseCount = agentResponseCountRef.current;

    sessionIdRef.current = null;
    isNewChatRef.current = true;
    newChatSessionIdRef.current = null;
    hasUpdatedUrl.current = false;
    // The counter tracks the CURRENT session's live response count —
    // owned in useWebSocket (not here) so it can be read above before
    // being zeroed; see agentResponseCountRef's own comment there.
    agentResponseCountRef.current = 0;

    generateTitleForAbandonedChat(leavingSessionId, leavingWasNewChat, leavingResponseCount);
  };

  return { pushSessionUrl, resetSessionForNewChat };
};
